import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { getDocuments, submitDocuments } from 'api/operations/documentsService';
import { Document, UploadStep } from 'types/documentsService';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import DocumentDelegate from './documentDelegate';

export interface PageContextParams {
  entityId: string;
  uploadStep: UploadStep;
  affiliatedId?: string;
}

export class UploadDocumentsModule {
  private _rootStore: RootStore = getRootStoreInstance();
  private _documents: DocumentDelegate[] = [];
  private _pageContextParams: PageContextParams;

  private _isLoading: boolean = false;

  public get activeDocuments(): DocumentDelegate[] {
    return this._documents.filter((document) => document.documentInfo.active === true);
  }

  public get inactiveDocuments(): DocumentDelegate[] {
    return this._documents.filter((document) => document.documentInfo.active === false);
  }

  public get documentsTitles(): string[] {
    return Array.from(new Set(this.activeDocuments.map((document) => document.documentInfo.type)));
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get documentsPassDomainRestrictions(): boolean {
    return this.activeDocuments.length < 1 || this.activeDocuments.every((doc) => doc.passedRestriction);
  }

  public getExistingDocumentTypes(): string[] {
    return this.documentsTitles;
  }

  private _createDocumentDelegates(documents: Document[]): void {
    const newDocuments: DocumentDelegate[] = [];
    documents.forEach((document) => {
      runInAction(() => {
        newDocuments.push(
          new DocumentDelegate({
            document,
            pageType: this._pageContextParams.uploadStep,
            userRoles: this._rootStore.userStore.userRoles,
            periodId: this._pageContextParams.affiliatedId || this._pageContextParams.entityId,
            refreshData: this.loadInitialData,
            setPageLoading: this.setIsLoading.bind(this),
            getExistingDocumentTypes: this.getExistingDocumentTypes.bind(this),
          }),
        );
      });
    });
    this._documents = newDocuments.sort((a, b) => a.documentInfo.type.localeCompare(b.documentInfo.type));
  }

  private async _uploadAndParseDocuments(): Promise<void> {
    const documents = await getDocuments(
      this._pageContextParams.entityId,
      this._pageContextParams.uploadStep,
      this._pageContextParams.affiliatedId || null,
    );
    runInAction(() => {
      this._createDocumentDelegates(documents);
    });
  }

  public async loadInitialData(): Promise<void> {
    this.setIsLoading(true);
    await this._uploadAndParseDocuments();
    this.setIsLoading(false);
  }

  public setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  /** Submits existing documents to the backend and bypass optional ones. */
  public async submitDocuments(): Promise<void> {
    await submitDocuments(this._pageContextParams.entityId, this._pageContextParams.uploadStep);
  }

  constructor(pageContextParams: PageContextParams) {
    this._pageContextParams = pageContextParams;
    makeObservable<
      UploadDocumentsModule,
      | '_pageContextParams'
      | '_documents'
      | '_uploadAndParseDocuments'
      | '_createDocumentDelegates'
      | '_rootStore'
      | '_isLoading'
    >(this, {
      _rootStore: observable,
      _pageContextParams: observable,
      _documents: observable,
      _isLoading: observable,

      activeDocuments: computed,
      inactiveDocuments: computed,
      documentsTitles: computed,
      isLoading: computed,
      documentsPassDomainRestrictions: computed,

      _createDocumentDelegates: action.bound,
      _uploadAndParseDocuments: action.bound,
      setIsLoading: action.bound,
      loadInitialData: action.bound,
      getExistingDocumentTypes: action.bound,
      submitDocuments: action.bound,
    });
  }
}
